// Copy/duplicate detection for PowerPoint slides, via hidden PowerPointApi 1.3 tags.
//
//   deck tag  (presentation)  SA_DECK_ID, SA_DECK_LOC   stays in the file when slides are copied out
//   slide tag (slide)         SA_SLIDE                  travels with the slide on copy/paste/duplicate
//
// Comparing the two on load tells us whether this slide is the original, a duplicate
// within the deck, or a paste from another deck. Tag keys are upper-cased by Office.

import { useOfficeStore } from '@store/officeStore';
import { cloneRosQuestion } from '@services/youtube-platform.service';

export const DECK_ID_TAG = 'SA_DECK_ID';
export const DECK_LOC_TAG = 'SA_DECK_LOC';
export const SLIDE_TAG = 'SA_SLIDE';

export interface SlideStamp {
  deckId: string;
  slideId: string;
  /** `selectedSlideId` from the add-in route, or the whole route when it has none yet. */
  interactionId: string;
  templateId?: string;
  /** Set on a fork: the stamp this slide was copied from. */
  forkedFrom?: SlideStamp;
}

export type SlideIdentity =
  | { kind: 'ok' }
  | { kind: 'mint-deck' }
  // Legacy slide with no stamp yet: keep its interaction and stamp it. Until stamped, a legacy
  // slide pasted from another deck can't be told apart from an original.
  | { kind: 'adopt' }
  | { kind: 'fork'; reason: 'duplicate-in-deck' | 'pasted-from-other-deck' };

export interface DeckStamp {
  deckId: string | null;
  location: string | null;
}

/** Pure decision table from the design; no Office calls so it can be unit tested. */
export const classifySlide = (
  deck: DeckStamp,
  slide: SlideStamp | null,
  currentSlideId: string,
): SlideIdentity => {
  if (!deck.deckId) return { kind: 'mint-deck' };
  if (!slide) return { kind: 'adopt' };
  if (slide.deckId !== deck.deckId) return { kind: 'fork', reason: 'pasted-from-other-deck' };
  if (slide.slideId !== currentSlideId) return { kind: 'fork', reason: 'duplicate-in-deck' };
  return { kind: 'ok' };
};

/**
 * Save As keeps the deck tag, so the stored location is compared to the current one.
 * An empty current location means the file is unsaved, which is never a move.
 */
export const hasDeckMoved = (stored: string | null, current: string | null): boolean =>
  !!stored && !!current && stored !== current;

export const isTagsApiSupported = (): boolean => {
  try {
    return Boolean(window.Office?.context?.requirements?.isSetSupported('PowerPointApi', '1.3'));
  } catch {
    return false;
  }
};

const getFileUrl = (): Promise<string | null> =>
  new Promise((resolve) => {
    window.Office.context.document.getFilePropertiesAsync((result) => {
      console.log('[deck-identity] getFilePropertiesAsync', result.status, result.value, result.error);
      resolve(result.status === Office.AsyncResultStatus.Succeeded ? result.value.url || null : null);
    });
  });

const readTags = async (
  tags: PowerPoint.TagCollection,
  context: PowerPoint.RequestContext,
): Promise<Record<string, string>> => {
  tags.load('items/key,items/value');
  await context.sync();
  return Object.fromEntries(tags.items.map((t) => [t.key, t.value]));
};

const parseSlideStamp = (raw: string | undefined): SlideStamp | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed?.deckId && parsed?.slideId ? parsed : null;
  } catch {
    return null;
  }
};

export interface DeckIdentityResult {
  identity: SlideIdentity;
  deckId: string;
  slideId: string;
  /** Previous stamp on the slide, so a fork can record forked_from. */
  previous: SlideStamp | null;
}

/**
 * Reads the deck and selected-slide stamps, resolves the deck id (minting one for a new,
 * legacy or Save-As'd file), and returns the classification. Writes only the deck tags;
 * the caller stamps the slide once it knows the interaction id (see {@link stampSlide}).
 * Returns null when tags are unsupported or the read fails.
 */
export const resolveSlideIdentity = async (): Promise<DeckIdentityResult | null> => {
  if (!isTagsApiSupported()) return null;

  try {
    const fileUrl = await getFileUrl();

    return await PowerPoint.run(async (context) => {
      const selected = context.presentation.getSelectedSlides();
      selected.load('items/id');
      await context.sync();
      const slide = selected.items[0];
      if (!slide) return null;

      const deckTags = await readTags(context.presentation.tags, context);
      const slideTags = await readTags(slide.tags, context);

      let deck: DeckStamp = {
        deckId: deckTags[DECK_ID_TAG] ?? null,
        location: deckTags[DECK_LOC_TAG] ?? null,
      };

      // Save As / moved file: re-mint the deck id so slides fork as they load.
      const moved = hasDeckMoved(deck.location, fileUrl);
      if (moved) deck = { deckId: null, location: fileUrl };

      const previous = parseSlideStamp(slideTags[SLIDE_TAG]);
      // A new or legacy deck keeps its slides (mint-deck); after a move, old stamps are foreign.
      let identity = classifySlide(deck, previous, slide.id);

      const deckId = deck.deckId ?? crypto.randomUUID();
      if (!deck.deckId) {
        context.presentation.tags.add(DECK_ID_TAG, deckId);
        if (moved) identity = classifySlide({ deckId, location: fileUrl }, previous, slide.id);
      }
      // First save of an unsaved file records its location; a move already reset it above.
      if (fileUrl && (!deck.location || moved)) {
        context.presentation.tags.add(DECK_LOC_TAG, fileUrl);
      }
      await context.sync();

      return {
        identity,
        deckId,
        slideId: slide.id,
        previous,
      };
    });
  } catch (error) {
    console.error('[deck-identity] resolve failed:', error);
    return null;
  }
};

/** Pulls the template and interaction IDs out of a saved add-in route, if it has them. */
export const parseInteractionRef = (
  route: string,
): { templateId?: string; interactionId: string } | null => {
  try {
    const params = new URL(route, 'http://x').searchParams;
    const interactionId = params.get('selectedSlideId');
    if (!interactionId) return null;
    return { templateId: params.get('templateId') ?? undefined, interactionId };
  } catch {
    return null;
  }
};

/**
 * Keeps an existing stamp's interaction fields current as the user navigates the add-in.
 * Only touches a stamp that belongs to this slide, so a pending fork is left alone.
 */
export const syncInteractionRef = async (route: string): Promise<void> => {
  const ref = parseInteractionRef(route);
  if (!ref || !isTagsApiSupported()) return;
  try {
    await PowerPoint.run(async (context) => {
      const selected = context.presentation.getSelectedSlides();
      selected.load('items/id');
      await context.sync();
      const slide = selected.items[0];
      if (!slide) return;
      const tags = await readTags(slide.tags, context);
      const stamp = parseSlideStamp(tags[SLIDE_TAG]);
      if (!stamp || stamp.slideId !== slide.id) return;
      if (stamp.interactionId === ref.interactionId && stamp.templateId === ref.templateId) return;
      slide.tags.add(SLIDE_TAG, JSON.stringify({ ...stamp, ...ref }));
      await context.sync();
    });
  } catch (error) {
    console.error('[deck-identity] sync failed:', error);
  }
};

/** Writes the slide stamp for the selected slide (overwrites an existing one). */
export const stampSlide = async (stamp: SlideStamp): Promise<void> => {
  if (!isTagsApiSupported()) return;
  await PowerPoint.run(async (context) => {
    const selected = context.presentation.getSelectedSlides();
    selected.load('items');
    await context.sync();
    selected.items[0]?.tags.add(SLIDE_TAG, JSON.stringify(stamp));
    await context.sync();
  });
};

/**
 * Resolves the selected slide's identity and reconciles it with `lastRoute`: stamps adopted
 * slides, and clones the interaction for a forked (duplicated / pasted) slide.
 * Returns the route to use, rewritten when a clone replaced the interaction.
 *
 * `forkOnly` is for the slide-change listener: this add-in instance can be alive while the
 * user selects an unrelated slide, so it acts only on a fork of its own interaction.
 */
export const reconcileSlideIdentity = async (
  lastRoute: string,
  opts: { forkOnly?: boolean } = {},
): Promise<{ route: string; changed: boolean }> => {
  const result = await resolveSlideIdentity();
  if (result) console.log('[deck-identity]', result);
  if (!result) return { route: lastRoute, changed: false };

  const { deckId, slideId, previous, identity } = result;
  const source = parseInteractionRef(lastRoute);

  if (identity.kind === 'ok') {
    if (!opts.forkOnly) await syncInteractionRef(lastRoute);
    return { route: lastRoute, changed: false };
  }
  if (opts.forkOnly) {
    const ownsFork =
      identity.kind === 'fork' && !!source && previous?.interactionId === source.interactionId;
    if (!ownsFork) return { route: lastRoute, changed: false };
  }

  let route = lastRoute;
  let changed = false;
  let stamp: SlideStamp | null = { deckId, slideId, interactionId: lastRoute, ...source };

  if (identity.kind === 'fork') {
    stamp = { ...stamp, ...(previous ? { forkedFrom: previous } : {}) };

    // The route travels with the slide, so it still names the source interaction.
    // A failed clone is left unstamped so the next open retries it.
    if (source) {
      stamp = null;
      useOfficeStore.getState().setIsCopyingSlide(true);
      try {
        const res = await cloneRosQuestion(source.interactionId);
        if (res?.status) {
          const { interactionId, templateId } = res.entity;
          const url = new URL(lastRoute, 'http://x');
          url.searchParams.set('selectedSlideId', interactionId);
          url.searchParams.set('templateId', templateId);
          route = `${url.pathname}${url.search}`;
          changed = true;
          await useOfficeStore.getState().saveSettings('lastRoute', route);
          stamp = {
            deckId,
            slideId,
            interactionId,
            templateId,
            ...(previous ? { forkedFrom: previous } : {}),
          };
        }
      } catch (error) {
        console.error('[deck-identity] clone failed:', error);
      } finally {
        useOfficeStore.getState().setIsCopyingSlide(false);
      }
    }
  }

  if (stamp) {
    try {
      await stampSlide(stamp);
    } catch (error) {
      console.error('[deck-identity] stamp failed:', error);
    }
  }
  return { route, changed };
};

// Dev-only console hook for the paste-survival test; remove once slides are stamped on load.
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  (window as any).__saDeckIdentity = { resolveSlideIdentity, stampSlide };
}
